import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react'

import { axiosDZAuthInstance } from '@/services/api/axios'
import { ERoleRequest } from '@/types/data'
import { useAuth } from './AuthContext'

/**
 * Roles held by people inside the company. Suppliers, merchants and external entities are
 * the ones outside it, and are the reason this exists.
 *
 * Written as an allowlist rather than a list of roles to shut out, so a role added later
 * has to be let in deliberately instead of reaching internal tooling by default.
 */
const INTERNAL_ROLES: string[] = [
  ERoleRequest.Admin,
  ERoleRequest.Superadmin,
  ERoleRequest.TZadmin,
  ERoleRequest.DATA_ANALYST,
  ERoleRequest.CC_TEAM,
]


const isInternalRole = (role: string | null): boolean => role !== null && INTERNAL_ROLES.includes(role.toUpperCase())

type TAdminRoleContext = {
  role: string | null
  isInternal: boolean
  isLoading: boolean
}

const AdminRoleContext = createContext<TAdminRoleContext | undefined>(undefined)

export const useAdminRole = (): TAdminRoleContext => {
  const context = useContext(AdminRoleContext)
  if (!context) {
    throw new Error('useAdminRole must be used within an AdminRoleProvider')
  }
  return context
}

/**
 * Makes the signed in admin's role available for gating internal only pages.
 *
 * Deliberately does not hold up the first render the way `AbilityProvider` does: mounted
 * above it, the request goes out alongside the permissions one instead of queueing behind
 * it, and consumers treat the loading state as "not allowed yet".
 */
export const AdminRoleProvider = ({ children }: PropsWithChildren) => {
  const { tokenParsed } = useAuth()
  const adminId = tokenParsed?.userId as string | undefined

  const [role, setRole] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!adminId) {
      setIsLoading(false)
      return
    }

    axiosDZAuthInstance
      .get<{ role?: string }>(`/dashboard/admins/${adminId}`)
      .then(({ data }) => setRole(data.role ?? null))
      .catch((error) => {
        console.error('Failed to fetch admin role:', error)
      })
      .finally(() => setIsLoading(false))
  }, [adminId])

  return (
    <AdminRoleContext.Provider value={{ role, isInternal: isInternalRole(role), isLoading }}>
      {children}
    </AdminRoleContext.Provider>
  )
}
